import React, { useCallback, useContext, useMemo } from 'react';

// DEPENDENCIES
import compact from 'lodash/compact';
import toString from 'lodash/toString';
import { compose } from 'redux';

// GLOBAL VARIABLES
import { NEW_USED, RETAIL_WHOLESALE, WHOLESALE_FORMAT } from 'codes';

// HELPERS
import { viewRedirect } from 'helpers/viewRedirect';

// HELPERS
import { RecordContext } from 'helpers/getRecordData';
import useRecordView from 'helpers/getRecordView';

// FUNCTIONS
import { sleep, checkRole } from 'functions.js';

// PROVIDERS
import DetailProvider from './helpers/DetailProvider';

// LOCAL COMPONENTS
import ProcessedThroughFinance from './sections/ProcessedThroughFinance';
import AMFinanceDetail from './parts/Detail';
import FMAssignment from './sections/fmAssignment/FMAssignment';
import FundingSource from './sections/FundingSource';
import AMFIProducts from './sections/AMFIProducts';
import DealDetails from './sections/dealDetails/DealDetails';
import ReviewSpotInstructions from './sections/ReviewSpotInstructions';
import DealCompletionChecklist from './sections/DealCompletionChecklist';
import MSRP from './sections/MSRP';
import InvoiceSA1 from './sections/InvoiceSA1';
import InvoiceSA2 from './sections/InvoiceSA2';
import InvoiceSA3 from './sections/InvoiceSA3';
import RebateAttachments from './sections/RebateAttachments';
import ProductsSoldExecutedAgreements from './sections/ProductsSoldExecutedAgreements';
import Other from './sections/Other';
import SaleVehicleInfo from './sections/SaleVehicleInfo';

// MAIN COMPONENT
const AMFinanceViewSections = compose(
  viewRedirect //
)(({ status, readOnly }) => {
  const {
    record: {
      inventory: { new_used } = {},
      aryRebate = [],
      wholesale_format_id,
      process_through_finance,
      sale_type_category_code,
      arySpotInstruction = [],
      aryChecklist = [],
      aryDealerAMFinance = []
    } = {}
  } = useContext(RecordContext) || {};

  const isReadOnly = readOnly || ['fallout', 'order'].includes(status);
  const isNew = new_used === NEW_USED.new.numeric;
  const isWholesale = sale_type_category_code === RETAIL_WHOLESALE.wholesale.category_code;
  const isWholesaleSimplified = isWholesale && wholesale_format_id === WHOLESALE_FORMAT.simplified.numeric;
  const requireCustomerSignature = useMemo(() => aryRebate.some(a => a.require_customer_signature_upload), [aryRebate]);
  const processThroughFinance = !isWholesaleSimplified || (isWholesaleSimplified && process_through_finance);
  const isFinanceManager = checkRole(['financeManager']);
  const { isOrder } = useRecordView();

  const sections = useMemo(
    () =>
      compact([
        processThroughFinance && FMAssignment,
        !isWholesaleSimplified && FundingSource,
        !isWholesaleSimplified && AMFIProducts,
        processThroughFinance && DealDetails,
        arySpotInstruction?.length && processThroughFinance && ReviewSpotInstructions,
        aryChecklist?.filter(a => a.is_required)?.length && processThroughFinance && DealCompletionChecklist
      ]),
    [processThroughFinance, isWholesaleSimplified, arySpotInstruction.length, aryChecklist]
  );

  const attachmentSections = useMemo(
    () =>
      compact([
        processThroughFinance && InvoiceSA1,
        isNew && !isWholesaleSimplified && MSRP,
        processThroughFinance && requireCustomerSignature && RebateAttachments,
        InvoiceSA2,
        processThroughFinance && InvoiceSA3,
        processThroughFinance &&
          aryDealerAMFinance?.filter(a => a.product_type === 0 && a.is_checked)?.length &&
          ProductsSoldExecutedAgreements,
        processThroughFinance && Other
      ]),
    [processThroughFinance, isNew, isWholesaleSimplified, requireCustomerSignature, aryDealerAMFinance]
  );

  const handleCompleted = useCallback(async ({ nextNumber, delay = 2000 }) => {
    if (delay) await sleep(delay);
    document.getElementById(toString(nextNumber)).scrollIntoView({ behavior: 'smooth' });
  }, []);

  return (
    <>
      {!isFinanceManager && (
        <>
          {isWholesaleSimplified ? <ProcessedThroughFinance /> : null}
          {sections.map((Component, i) => (
            <Component key={i} number={i + 1} onCompleted={handleCompleted} readOnly={isReadOnly} />
          ))}
          {attachmentSections.map((Component, i) => (
            <Component key={i} number={processThroughFinance ? i + 1 + sections.length : undefined} />
          ))}
        </>
      )}
      <SaleVehicleInfo isOrder={isOrder} isFinanceManager={isFinanceManager}/>
    </>
  );
});

// EXPORT
export default function AMFinanceView(props) {
  return (
    <DetailProvider>
      <AMFinanceDetail>
        <AMFinanceViewSections {...props} />
      </AMFinanceDetail>
    </DetailProvider>
  );
}
